import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Award, CheckCircle, Clock, Briefcase, TrendingUp, X } from 'lucide-react';
import axios from 'axios';
import VolunteerTasks from './VolunteerTasks';
import { useAuth } from '../../Contexts/AuthContext/AuthContext';
import { getAuthToken } from '../../services/api';
import './vStyles/VolunteerDashboard.css';
import ProfileModal from '../../Components/Profile/ProfileModal';

interface Project {
  id: number;
  name: string;
  description: string;
  status: 'Active' | 'Completed' | 'Unassigned';
  created_at: string;
  is_joined?: boolean;
}

export interface Task {
  id: number;
  title: string;
  description: string;
  project_id: number;
  project_name?: string;
  required_skills: string[];
  location: string;
  due_date?: string;
  applied?: boolean;
}

interface VolunteerProfile {
  firstname: string;
  surname: string;
  email: string;
  created_at: string;
}

export default function VolunteerDashboard() {
  const [allActiveProjects, setAllActiveProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<VolunteerProfile | null>(null);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [appliedTasks, setAppliedTasks] = useState<Task[]>([]);
  const [availableTasks, setAvailableTasks] = useState<Task[]>([]);
  const [myTasks, setMyTasks] = useState<any[]>([]);
  const [badgeData, setBadgeData] = useState<{
    current_badge: string;
    next_badge: string;
    completed_tasks: number;
    progress_percentage: number;
    tasks_remaining: number;
  } | null>(null);
  
  const { volunteerId } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

  const checkFirstLogin = () => {
    const hasLoggedInBefore = localStorage.getItem('hasLoggedInBefore');
    if (!hasLoggedInBefore) {
      setIsFirstLogin(true);
      localStorage.setItem('hasLoggedInBefore', 'true');
    }
  };

  const fetchVolunteerProfile = async () => {
    if (!volunteerId) return;
    
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/volunteers/${volunteerId}`, { headers });
      setProfile(response.data as VolunteerProfile);
    } catch (error) {
      console.error('Error fetching volunteer profile:', error);
    }
  };

  const fetchJoinedProjects = async () => {
    if (!volunteerId) return [];
    
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/volunteer/${volunteerId}/joined-projects`, { headers });
      return (response.data as Project[]).map((p: Project) => p.id);
    } catch (error) {
      console.error('Error fetching joined projects:', error);
      return [];
    }
  };

  const fetchAppliedTasks = async (): Promise<number[]> => {
    if (!volunteerId) return [];
    
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = {
        'user-id': volunteerId.toString(),
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/volunteer/${volunteerId}/applied-tasks`, { headers });
      return response.data as number[];
    } catch (error) {
      console.error('Error fetching applied tasks:', error);
      return [];
    }
  };

  const fetchMyTasks = async () => {
    if (!volunteerId) return;
    
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = {
        'user-id': volunteerId.toString(),
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/volunteer/${volunteerId}/my-tasks`, { headers });
      setMyTasks(response.data as any[]);
    } catch (error) {
      console.error('Error fetching my tasks:', error);
      setMyTasks([]);
    }
  };

  const fetchBadgeData = async () => {
    if (!volunteerId) return;
    
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = {
        'user-id': volunteerId.toString(),
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/volunteer/${volunteerId}/task-stats`, { headers });
      setBadgeData(response.data as {
        current_badge: string;
        next_badge: string;
        completed_tasks: number;
        progress_percentage: number;
        tasks_remaining: number;
      });
    } catch (error) {
      console.error('Error fetching badge data:', error);
      // Set default badge data when endpoint is not available
      setBadgeData({
        current_badge: 'None',
        next_badge: 'Bronze', 
        completed_tasks: 0,
        progress_percentage: 0,
        tasks_remaining: 10
      });
    }
  };

  const fetchVolunteerTasks = useCallback(async () => {
    if (!volunteerId) return;
    
    try {
      const token = getAuthToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'user-id': volunteerId.toString()
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Try multiple endpoints
      let tasks: Task[] = [];
      try {
        const response = await axios.get<Task[]>(`${API_BASE_URL}/api/volunteer/tasks?volunteerId=${volunteerId}`, { headers });
        tasks = response.data;
      } catch (error) {
        console.log('Main task endpoint failed, tasks will be empty');
        // If main endpoint fails, tasks will remain empty
        tasks = [];
      }

      const appliedTaskIds = await fetchAppliedTasks();
      
      const applied = tasks.filter(task => appliedTaskIds.includes(task.id));
      const available = tasks.filter(task => !appliedTaskIds.includes(task.id));
      
      setAppliedTasks(applied);
      setAvailableTasks(available);
    } catch (error) {
      console.error('Error fetching volunteer tasks:', error);
      // Set empty arrays instead of failing completely
      setAppliedTasks([]);
      setAvailableTasks([]);
    }
  }, [volunteerId]);

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      await fetchVolunteerProfile();
      
      const token = getAuthToken();
      const headers: Record<string, string> = {
        'user-type': 'volunteer',
        'user-id': volunteerId?.toString() || '1'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const projectsResponse = await axios.get<Project[]>(`${API_BASE_URL}/api/projects`, { headers });
      
      // Get all active and completed projects the volunteer can see
      const allProjects = projectsResponse.data.filter(p => p.status === 'Active' || p.status === 'Completed');
      const joinedProjectIds = await fetchJoinedProjects();
      
      const projectsWithStatus = allProjects.map(project => ({
        ...project,
        is_joined: joinedProjectIds.includes(project.id)
      }));
      
      setAllActiveProjects(projectsWithStatus);
      await fetchVolunteerTasks();
      await fetchMyTasks();
      await fetchBadgeData();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [volunteerId, fetchVolunteerTasks]);

  useEffect(() => {
    fetchAllData();
    checkFirstLogin();
  }, [fetchAllData]);

  const handleJoinProject = async (project: Project) => {
    try {
      if (!volunteerId) {
        alert('Please log in to join projects');
        return;
      }
      
      await axios.post(`${API_BASE_URL}/api/projects/${project.id}/join`, {
        volunteerId: volunteerId
      }, {
        headers: {
          'user-id': volunteerId.toString(),
          'Content-Type': 'application/json'
        }
      });
      
      // Refresh the joined projects after successful join
      await fetchAllData();
      alert(`Successfully joined "${project.name}"!`);
      
    } catch (error) {
      console.error('Error joining project:', error);
      alert('Failed to join project. Please try again.');
    }
  };

  const handleLeaveProject = async (project: Project) => {
    try {
      await axios.post(`${API_BASE_URL}/api/projects/${project.id}/leave`);
      
      // Refresh the joined projects after successful leave
      await fetchAllData();
      alert(`Left "${project.name}" successfully.`);
      
    } catch (error) {
      console.error('Error leaving project:', error);
      alert('Failed to leave project. Please try again.');
    }
  };

  const handleWithdrawTask = async (taskId: number) => {
    try {
      if (!volunteerId) {
        alert('Please log in to withdraw from tasks');
        return;
      }
      
      if (!window.confirm('Are you sure you want to withdraw from this task?')) {
        return;
      }
      
      await axios.put(`${API_BASE_URL}/api/volunteer/${volunteerId}/tasks/${taskId}/withdraw`, {}, {
        headers: {
          'user-id': volunteerId.toString(),
          'Content-Type': 'application/json'
        }
      });
      
      alert('Successfully withdrew from the task.');
      fetchAllData();
      
    } catch (error) {
      console.error('Error withdrawing from task:', error);
      alert('Failed to withdraw from task. Please try again.');
    }
  };

  const getUserInitials = () => {
    const userName = profile ? `${profile.firstname} ${profile.surname}` : "Volunteer User";
    return userName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleViewProject = (project: Project) => {
    setSelectedProject(prev => 
      prev?.id === project.id ? null : project
    );
  };

  const welcomeMessage = isFirstLogin 
    ? `Welcome to Your Volunteer Journey, ${profile?.firstname || 'Volunteer'}!` 
    : `Welcome Back, ${profile?.firstname || 'Volunteer'}!`;

  // Calculate stats
  const completedTasksCount = myTasks.filter(t => t.status === 'completed').length;
  const ongoingTasksCount = myTasks.filter(t => t.status === 'ongoing').length;
  const joinedProjectsCount = allActiveProjects.filter(p => p.is_joined).length;

  return (
    <div className="volunteer-dashboard-redesigned">
      {/* Header */}
      <header className="v-dashboard-header">
        <div className="v-header-content">
          <h1 className="v-dashboard-title">{welcomeMessage}</h1>
          <p className="v-dashboard-subtitle">Track your impact and explore new opportunities</p>
        </div>
        <button 
          onClick={() => setIsProfileModalOpen(true)}
          className="v-profile-button"
          aria-label="Open profile menu"
          title="My Profile"
        >
          {getUserInitials()}
        </button>
      </header>

      <ProfileModal 
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />

      {/* Stats Overview */}
      <div className="v-stats-overview">
        <div className="v-stat-card v-stat-completed">
          <div className="v-stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="v-stat-info">
            <h3>{completedTasksCount}</h3>
            <p>Completed Tasks</p>
          </div>
        </div>

        <div className="v-stat-card v-stat-ongoing">
          <div className="v-stat-icon">
            <Clock size={24} />
          </div>
          <div className="v-stat-info">
            <h3>{ongoingTasksCount}</h3>
            <p>Ongoing Tasks</p>
          </div>
        </div>

        <div className="v-stat-card v-stat-projects">
          <div className="v-stat-icon">
            <Briefcase size={24} />
          </div>
          <div className="v-stat-info">
            <h3>{joinedProjectsCount}</h3>
            <p>Joined Projects</p>
          </div>
        </div>

        <div className="v-stat-card v-stat-applied">
          <div className="v-stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="v-stat-info">
            <h3>{appliedTasks.length}</h3>
            <p>Applied Tasks</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="v-dashboard-grid">
        {/* Left Column - Tasks */}
        <div className="v-left-column">
          {/* Suggested Tasks */}
          <div className="v-card suggested-tasks-card">
            <div className="v-card-header">
              <h2>Suggested Tasks</h2>
              <span className="v-badge">{availableTasks.length} Available</span>
            </div>
            <div className="v-card-content">
              <VolunteerTasks />
            </div>
          </div>

          {/* Applied Tasks */}
          <div className="v-card applied-tasks-card">
            <div className="v-card-header">
              <h2>Applied Tasks</h2>
              <span className="v-badge v-badge-blue">{appliedTasks.length}</span>
            </div>
            <div className="v-card-content">
              {appliedTasks.length === 0 ? (
                <div className="v-empty-state">
                  <CheckCircle size={48} strokeWidth={1.5} />
                  <p>No applied tasks</p>
                  <span>Apply to tasks to see them here</span>
                </div>
              ) : (
                <div className="v-tasks-list">
                  {appliedTasks.map((task) => (
                    <div key={task.id} className="v-task-item">
                      <div className="v-task-info">
                        <h4 className="v-task-title">{task.title}</h4>
                        <p className="v-task-description">{task.description}</p>
                        <div className="v-task-meta">
                          <span className="v-task-project">Project: {task.project_name}</span>
                          <span className="v-task-location">📍 {task.location}</span>
                          {task.due_date && (
                            <span className="v-task-due">📅 {new Date(task.due_date).toLocaleDateString()}</span>
                          )}
                        </div>
                        <div className="v-task-skills">
                          {task.required_skills?.map((skill, index) => (
                            <span key={index} className="v-skill-tag">{skill}</span>
                          ))}
                        </div>
                      </div>
                      <div className="v-task-actions">
                        <span className="v-applied-badge">Applied</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* My Tasks */}
          <div className="v-card my-tasks-card">
            <div className="v-card-header">
              <h2>My Tasks</h2>
              <span className="v-badge v-badge-green">{myTasks.length}</span>
            </div>
            <div className="v-card-content">
              {myTasks.length === 0 ? (
                <div className="v-empty-state">
                  <CheckCircle size={48} strokeWidth={1.5} />
                  <p>No assigned tasks</p>
                  <span>Tasks you're approved for will appear here</span>
                </div>
              ) : (
                <div className="v-my-tasks-list">
                  {myTasks.map(task => (
                    <div key={task.id} className={`v-my-task-item ${task.status}`}>
                      <div className="v-task-main">
                        <div className="v-task-info">
                          <h3>{task.title}</h3>
                          <p className="v-task-meta">
                            <Briefcase size={14} /> Project: {task.project_name}
                          </p>
                          <p className="v-task-date">
                            Applied: {new Date(task.applied_at).toLocaleDateString()}
                          </p>
                          {task.due_date && (
                            <p className="v-task-date">
                              Due: {new Date(task.due_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div className="v-task-actions">
                          <span className={`v-status-badge ${task.status}`}>
                            {task.status === 'completed' ? '✓ Completed' : '⟳ Ongoing'}
                          </span>
                          {task.status === 'ongoing' && (
                            <button 
                              onClick={() => handleWithdrawTask(task.id)}
                              className="v-withdraw-btn"
                              title="Withdraw from this task"
                            >
                              <X size={16} /> Withdraw
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Achievements & Projects */}
        <div className="v-right-column">
          {/* Achievements */}
          <div className="v-card achievements-card">
            <div className="v-card-header">
              <h2>Achievements</h2>
              <Award size={20} />
            </div>
            <div className="v-card-content">
              <div className="v-achievement-display">
                <div className="v-badge-large">
                  <Award size={48} />
                </div>
                {badgeData ? (
                  <>
                    <h3>{badgeData.current_badge === 'None' ? 'No Badge Yet' : `${badgeData.current_badge} Volunteer`}</h3>
                    <p>You've completed {badgeData.completed_tasks} tasks!</p>
                    {badgeData.current_badge !== 'Platinum' ? (
                      <>
                        <div className="v-progress-bar">
                          <div className="v-progress-fill" style={{ width: `${badgeData.progress_percentage}%` }}></div>
                        </div>
                        <p className="v-progress-text">
                          {badgeData.tasks_remaining} more {badgeData.tasks_remaining === 1 ? 'task' : 'tasks'} to reach {badgeData.next_badge} 
                          ({Math.round(badgeData.progress_percentage)}% complete)
                        </p>
                      </>
                    ) : (
                      <p style={{ color: '#10b981', fontWeight: 'bold', marginTop: '0.5rem' }}>
                        🎉 Maximum Achievement Reached!
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <h3>No Badge Yet</h3>
                    <p>Complete tasks to earn your first badge!</p>
                    <div className="v-progress-bar">
                      <div className="v-progress-fill" style={{ width: '0%' }}></div>
                    </div>
                    <p className="v-progress-text">Almost there to reach Bronze!</p>
                  </>
                )}
              </div>
              <Link to="/volunteer/badges" className="v-view-all-btn">
                View All Achievements
              </Link>
            </div>
          </div>

          {/* Available Projects */}
          <div className="v-card projects-card">
            <div className="v-card-header">
              <h2>Available Projects</h2>
              <span className="v-badge">{allActiveProjects.length}</span>
            </div>
            <div className="v-card-content">
              {loading ? (
                <div className="v-loading">
                  <div className="v-spinner"></div>
                  <p>Loading projects...</p>
                </div>
              ) : allActiveProjects.length === 0 ? (
                <div className="v-empty-state">
                  <Briefcase size={48} strokeWidth={1.5} />
                  <p>No active projects</p>
                  <span>Check back later for new opportunities</span>
                </div>
              ) : (
                <div className="v-projects-list">
                  {allActiveProjects.slice(0, 3).map(project => (
                    <div key={project.id} className="v-project-card">
                      <div className="v-project-header">
                        <div className="v-project-title-row">
                          <h4>{project.name}</h4>
                          {project.is_joined && (
                            <span className="v-joined-badge">Joined</span>
                          )}
                        </div>
                        <button 
                          onClick={() => handleViewProject(project)}
                          className="v-view-toggle"
                        >
                          <Eye size={16} />
                        </button>
                      </div>

                      {selectedProject?.id === project.id && (
                        <div className="v-project-details">
                          <p className="v-project-description">{project.description}</p>
                          <div className="v-project-meta">
                            <span className="v-project-date">
                              Created: {new Date(project.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="v-project-buttons">
                            <Link 
                              to={`/volunteer/projects/${project.id}/tasks`}
                              className="v-view-tasks-btn"
                            >
                              View Tasks
                            </Link>
                            {!project.is_joined ? (
                              <button 
                                onClick={() => handleJoinProject(project)}
                                className="v-join-btn"
                              >
                                Join Project
                              </button>
                            ) : (
                              <button 
                                onClick={() => handleLeaveProject(project)}
                                className="v-leave-btn"
                              >
                                Leave Project
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {allActiveProjects.length > 3 && (
                <Link to="/volunteer/projects" className="v-view-all-link">
                  View All Projects →
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}